// Fiók-űrlap tiszta segédei (nem-komponens): form-értékek típusa, üres (create) alapértékek, a select-mezők
// „nincs" sentinelje, a betöltött detail → form-értékek leképezés, a form → PATCH-body (edit) és a form →
// CREATE-body leképezés, a @atlas/contracts sémáiból származtatott resolver-séma, és a lokalizált zod-hibatérkép.
// Külön fájl a komponenstől, hogy a react-refresh (Fast Refresh) tiszta maradjon (az account-form.tsx CSAK a
// komponenst exportálja).
import { type AccountCreateBodySchema, type AccountUpdateBodySchema } from '@atlas/contracts';
import {
  ACCOUNT_DOMAIN_STATUSES,
  domainKey,
  normalizeDomainToken,
  type AccountDomainStatus,
  INTEGRATION_EXTERNAL_ID_MAX,
  INTEGRATION_NOTE_MAX,
  INTEGRATION_SYSTEMS,
  INTEGRATIONS_MAX,
  type AccountIntegration,
  type ClientStatus,
  type IntegrationSystem,
} from '@atlas/shared';
import { type TFunction } from 'i18next';
import { z } from 'zod';

/**
 * A select-mezők „nincs érték" sentinelje. A Radix Select nem enged üres-string `value`-t, ezért az
 * opcionális (nullable) select-mezők üres állapotát ezzel a rejtett kulccsal képezzük; a body-összeállításkor
 * visszaalakítjuk üres stringgé (a contract preprocess-e onnan null-ra). A többválasztós `types` NEM select
 * (üresen sima üres tömb), és a `roomCount` sem (üresen üres string).
 */
export const ACCOUNT_SELECT_NONE = '__none__';

/**
 * Egy integráció-sor a form-ban: a belső `id` (új sornál a kliens generálja — erre hivatkozik majd a
 * hozzárendelés kötése), a rendszer, a külső azonosító és a megjegyzés (üres = nincs).
 */
export interface AccountIntegrationFormRow {
  id: string;
  system: IntegrationSystem;
  externalId: string;
  note: string;
}

/** Új, üres integráció-sor (a „+ Azonosító" gomb) — friss UUID, a rendszer az első választható. */
export function newIntegrationRow(
  system: IntegrationSystem = 'hotelcms',
): AccountIntegrationFormRow {
  return { id: crypto.randomUUID(), system, externalId: '', note: '' };
}

/**
 * Egy domain-sor a form-ban (a „Domainek" szekció — a rendszerazonosítók mintájára, a lap Mentés-gombjával).
 * `domainId` = a meglévő sor azonosítója (null → új sor; az azonosítót a szerver adja, ezért mentés után a
 * form a szerver-válaszból töltődik újra).
 */
export interface AccountDomainFormRow {
  domainId: string | null;
  domain: string;
  status: AccountDomainStatus;
}

/** Új, üres domain-sor (a „+ Domain felvétele" gomb) — alapból aktív (user-döntés). */
export function newDomainRow(): AccountDomainFormRow {
  return { domainId: null, domain: '', status: 'active' };
}

/** A CRM-fiók (writable) mező-értékei — string-alapú form (a select-mezők a NONE sentinelt hordozzák üresen). */
export interface AccountFormValues {
  name: string;
  status: ClientStatus;
  website: string;
  /** Több-választós Típus/kategória (a régi single `type` fejlődménye) — a KÓD-ok tömbje. */
  types: string[];
  /** Besorolás (A++/A+/A/B/C) — single select, NONE sentinellel. */
  category: string;
  /** Régió KÓD — single select, NONE sentinellel. */
  region: string;
  /** Szobaszám — a form-oldalon STRING (üres = nincs); a body-map alakítja number|null-ra. */
  roomCount: string;
  /** A szülő-ügyfél egy számlázási profiljának id-ja — single select, NONE sentinellel (= ügyfél elsődlegese). */
  billingProfileId: string;
  /** A fiók SAJÁT számlázása (a legacy `override_billing`): felülírja az ügyfél elsődleges profilját, ÉS
   *  önálló számlát ad a fiók-szintű hozzárendeléseknek (különben az ügyfél számlájába olvadnak). */
  overrideBilling: boolean;
  countryCode: string;
  countryName: string;
  zip: string;
  city: string;
  address: string;
  note: string;
  bookingEngine: string;
  pms: string;
  channelManager: string;
  emailWebHosting: string;
  asanaName: string;
  /** A külső rendszer-azonosítók listája (rendszerenként több is, megjegyzéssel). */
  integrations: AccountIntegrationFormRow[];
  /** A fiók (nem-fő) domainjei állapottal — a fő domain a `website`. */
  domains: AccountDomainFormRow[];
  // ── LEGACY-PARITÁS mezők (v4 séma). A koordináta a form-oldalon STRING (a contract is stringet
  //    kér — lossless decimal); a három flag boolean; a három kulcs-lista KÓD-tömb (mint a `types`). ──
  latitude: string;
  longitude: string;
  cloudflare: boolean;
  autoBilling: boolean;
  exportCampaigns: string[];
  smtps: string[];
  mailings: string[];
}

/** Üres (create) alapértékek — a select-mezők a NONE sentinelen, a `types` üres tömbön, a `roomCount` üres stringen. */
export const EMPTY_ACCOUNT_FORM: AccountFormValues = {
  name: '',
  status: 'active',
  website: '',
  types: [],
  category: ACCOUNT_SELECT_NONE,
  region: ACCOUNT_SELECT_NONE,
  roomCount: '',
  billingProfileId: ACCOUNT_SELECT_NONE,
  overrideBilling: false,
  countryCode: '',
  countryName: '',
  zip: '',
  city: '',
  address: '',
  note: '',
  latitude: '',
  longitude: '',
  cloudflare: false,
  autoBilling: false,
  exportCampaigns: [],
  smtps: [],
  mailings: [],
  bookingEngine: ACCOUNT_SELECT_NONE,
  pms: ACCOUNT_SELECT_NONE,
  channelManager: ACCOUNT_SELECT_NONE,
  emailWebHosting: ACCOUNT_SELECT_NONE,
  asanaName: '',
  integrations: [],
  domains: [],
};

/** A resolver-séma: a form-input (mind string / string-tömb) validálása — a name kötelező, a status a CRM-enum,
 *  a roomCount üres VAGY nem-negatív egész (a szám-konverzió a body-map-ben). */
export const accountFormSchema = z.object({
  name: z.string().trim().min(1).max(255),
  status: z.enum(['active', 'pending', 'inactive']),
  website: z.string().max(255),
  types: z.array(z.string()),
  category: z.string(),
  region: z.string(),
  roomCount: z.string().refine((v) => v.trim() === '' || /^\d+$/.test(v.trim())),
  billingProfileId: z.string(),
  overrideBilling: z.boolean(),
  countryCode: z.string().max(8),
  countryName: z.string().max(128),
  zip: z.string().max(32),
  city: z.string().max(128),
  address: z.string().max(255),
  note: z.string().max(4000),
  bookingEngine: z.string(),
  pms: z.string(),
  channelManager: z.string(),
  emailWebHosting: z.string(),
  asanaName: z.string().max(255),
  // a külső azonosító kötelező (az üres sor nem azonosító — törölni kell); a megjegyzés opcionális
  integrations: z
    .array(
      z.object({
        id: z.string(),
        system: z.enum(INTEGRATION_SYSTEMS),
        externalId: z.string().trim().min(1).max(INTEGRATION_EXTERNAL_ID_MAX),
        note: z.string().max(INTEGRATION_NOTE_MAX),
      }),
    )
    .max(INTEGRATIONS_MAX),
  // ── LEGACY-PARITÁS (v4). A koordináta STRING: üres VAGY `decimal(10,6)`-alak (max 6 tizedes), ±180-on
  //    belül — ugyanaz a szabály, mint a contract `nullableCoordinate`-jében (a mező-hiba így a mezőnél jön).
  latitude: z
    .string()
    .refine((v) => v.trim() === '' || /^-?\d{1,3}(\.\d{1,6})?$/.test(v.trim()))
    .refine((v) => v.trim() === '' || Math.abs(Number(v)) <= 90),
  longitude: z
    .string()
    .refine((v) => v.trim() === '' || /^-?\d{1,3}(\.\d{1,6})?$/.test(v.trim()))
    .refine((v) => v.trim() === '' || Math.abs(Number(v)) <= 180),
  cloudflare: z.boolean(),
  autoBilling: z.boolean(),
  exportCampaigns: z.array(z.string()),
  smtps: z.array(z.string()),
  mailings: z.array(z.string()),
  // a domain kötelező és domain-alakú (a közös szabály — ugyanaz, amit a szerver normalizál); a duplikátum a
  // `accountFormSchemaWithRules`-ban (a kanonikus kulcson)
  domains: z.array(
    z.object({
      domainId: z.string().nullable(),
      domain: z
        .string()
        .trim()
        .min(1)
        .refine((v) => 'domain' in normalizeDomainToken(v), { params: { domainInvalid: true } }),
      status: z.enum(ACCOUNT_DOMAIN_STATUSES),
    }),
  ),
});

/**
 * A legacy `required_with` szabály (`AccountManagement.php:406-455`): a szélesség és a hosszúság
 * KÖLCSÖNÖSEN kötelező — az egyik önmagában értelmetlen (fél-koordináta nem hely). A hiba MINDKÉT mezőre
 * kerül, hogy a felhasználó lássa, melyiket kell kitöltenie.
 */
export const accountFormSchemaWithRules = accountFormSchema.superRefine((v, ctx) => {
  // ugyanaz a domain kétszer — a NORMALIZÁLT alak kanonikus kulcsán (a `https://` és a kis/nagybetű nem számít)
  const seen = new Set<string>();
  v.domains.forEach((d, i) => {
    const r = normalizeDomainToken(d.domain);
    if (!('domain' in r)) return;
    const key = domainKey(r.domain);
    if (seen.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['domains', i, 'domain'],
        params: { domainDuplicate: true },
      });
    }
    seen.add(key);
  });
  const lat = v.latitude.trim();
  const lng = v.longitude.trim();
  if ((lat === '') === (lng === '')) return;
  for (const path of ['latitude', 'longitude'] as const) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], params: { coordinatePair: true } });
  }
});

const normStatus = (v: string): ClientStatus =>
  v === 'pending' || v === 'inactive' ? v : 'active';

/** A detail (nullable) mezői — a form-előtöltés bemenete (az AccountDetail writable + új mezői). */
interface AccountFormSource {
  name: string;
  status: string;
  website: string | null;
  category: string | null;
  region: string | null;
  roomCount: number | null;
  types: string[];
  billingProfileId: string | null;
  overrideBilling: boolean;
  countryCode: string | null;
  countryName: string | null;
  zip: string | null;
  city: string | null;
  address: string | null;
  note: string | null;
  bookingEngine: string | null;
  pms: string | null;
  channelManager: string | null;
  emailWebHosting: string | null;
  asanaName: string | null;
  integrations: AccountIntegration[];
  /** A „Domainek" szekció sorai (a detail `accountDomains`-e; a create-űrlapnál nincs). */
  accountDomains?: { id: string; domain: string; status: AccountDomainStatus }[];
  latitude: string | null;
  longitude: string | null;
  cloudflare: boolean;
  autoBilling: boolean;
  exportCampaigns: string[];
  smtps: string[];
  mailings: string[];
}

/** A betöltött detail → form-értékek: a null-t üres stringre, az üres select-mezőt a NONE sentinelre, a
 *  roomCount-ot stringre, a types-t tömbre képezi. */
export function accountToFormValues(body: AccountFormSource): AccountFormValues {
  const sel = (v: string | null): string => (v && v.trim() !== '' ? v : ACCOUNT_SELECT_NONE);
  return {
    name: body.name,
    status: normStatus(body.status),
    website: body.website ?? '',
    types: body.types,
    category: sel(body.category),
    region: sel(body.region),
    roomCount: body.roomCount != null ? String(body.roomCount) : '',
    billingProfileId: sel(body.billingProfileId),
    overrideBilling: body.overrideBilling,
    countryCode: body.countryCode ?? '',
    countryName: body.countryName ?? '',
    zip: body.zip ?? '',
    city: body.city ?? '',
    address: body.address ?? '',
    note: body.note ?? '',
    bookingEngine: sel(body.bookingEngine),
    pms: sel(body.pms),
    channelManager: sel(body.channelManager),
    emailWebHosting: sel(body.emailWebHosting),
    asanaName: body.asanaName ?? '',
    domains: (body.accountDomains ?? []).map((d) => ({
      domainId: d.id,
      domain: d.domain,
      status: d.status,
    })),
    integrations: body.integrations.map((i) => ({
      id: i.id,
      system: i.system,
      externalId: i.externalId,
      note: i.note ?? '',
    })),
    latitude: body.latitude ?? '',
    longitude: body.longitude ?? '',
    cloudflare: body.cloudflare,
    autoBilling: body.autoBilling,
    exportCampaigns: body.exportCampaigns,
    smtps: body.smtps,
    mailings: body.mailings,
  };
}

/** Select-mező NONE sentinel → üres string (a contract preprocess-e onnan null-ra). */
const selOut = (v: string): string => (v === ACCOUNT_SELECT_NONE ? '' : v);
/** Szobaszám: üres string → null, egyébként szám (a contract nem-negatív egészre validál). */
const roomOut = (v: string): number | null => (v.trim() === '' ? null : Number(v.trim()));

/** A form-értékek → a `clients/account` writable mezői (a create és az update KÖZÖS magja). */
function accountFormToWritable(values: AccountFormValues): {
  name: string;
  status: ClientStatus;
  website: string;
  types: string[];
  category: string;
  region: string;
  roomCount: number | null;
  billingProfileId: string;
  countryCode: string;
  countryName: string;
  zip: string;
  city: string;
  address: string;
  note: string;
  bookingEngine: string;
  pms: string;
  channelManager: string;
  emailWebHosting: string;
  asanaName: string;
  integrations: { id: string; system: IntegrationSystem; externalId: string; note: string }[];
  domains: { id?: string; domain: string; status: AccountDomainStatus }[];
  latitude: string;
  longitude: string;
  cloudflare: boolean;
  autoBilling: boolean;
  overrideBilling: boolean;
  exportCampaigns: string[];
  smtps: string[];
  mailings: string[];
} {
  return {
    name: values.name,
    status: values.status,
    website: values.website,
    types: values.types,
    category: selOut(values.category),
    region: selOut(values.region),
    roomCount: roomOut(values.roomCount),
    billingProfileId: selOut(values.billingProfileId),
    overrideBilling: values.overrideBilling,
    countryCode: values.countryCode,
    countryName: values.countryName,
    zip: values.zip,
    city: values.city,
    address: values.address,
    note: values.note,
    bookingEngine: selOut(values.bookingEngine),
    pms: selOut(values.pms),
    channelManager: selOut(values.channelManager),
    emailWebHosting: selOut(values.emailWebHosting),
    // szabad string (üres → a contract nullableStr preprocess-e null-ra)
    asanaName: values.asanaName,
    // a domain-lista a VÉGÁLLAPOT (ami nincs benne, lekerül); a meglévő sor az azonosítójával megy
    domains: values.domains.map((d) => ({
      ...(d.domainId ? { id: d.domainId } : {}),
      domain: d.domain.trim(),
      status: d.status,
    })),
    // a TELJES lista megy (nem patch); trimmelve, az üres megjegyzés a contract preprocess-ével null lesz
    integrations: values.integrations.map((i) => ({
      id: i.id,
      system: i.system,
      externalId: i.externalId.trim(),
      note: i.note.trim(),
    })),
    // ── LEGACY-PARITÁS (v4). A koordináta trimmelve megy (üres → a contract preprocess-e null-ra); a
    //    flagek boolean; a kulcs-listák tömbként (mint a `types`).
    latitude: values.latitude.trim(),
    longitude: values.longitude.trim(),
    cloudflare: values.cloudflare,
    autoBilling: values.autoBilling,
    exportCampaigns: values.exportCampaigns,
    smtps: values.smtps,
    mailings: values.mailings,
  };
}

/**
 * A form-értékek → PATCH-body (EDIT). A select-mezők NONE sentinelje üres stringgé (a contract preprocess-e
 * onnan null-ra), a `roomCount` number|null-ra, a `types` tömbként. A régi single `type` mezőt NEM küldjük (a
 * részleges PATCH megtartja a meglévő data-értéket). A body a contract AccountUpdateBodySchema-jával kompatibilis.
 */
export function accountFormToBody(
  values: AccountFormValues,
): z.input<typeof AccountUpdateBodySchema> {
  return accountFormToWritable(values);
}

/**
 * A form-értékek → CREATE-body. Ugyanaz a writable mag + a szülő-ügyfél `clientId`-je (a user választja). A régi
 * single `type` NINCS a create-body-ban (a UI a `types`-t szerkeszti). A body a contract AccountCreateBodySchema-
 * jával kompatibilis.
 */
export function accountFormToCreateBody(
  values: AccountFormValues,
  clientId: string,
): z.input<typeof AccountCreateBodySchema> {
  return { clientId, ...accountFormToWritable(values) };
}

/**
 * Lokalizált zod-hibatérkép: a zod-issue (mező-path + kód) → a `validation:accounts.*` réteg üzenete.
 * Ismeretlen issue → a zod alapértelmezett üzenete. Ugyanabból a rétegből fordít, mint a szerver 422-terelése.
 */
export function buildAccountErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'name') {
      if (issue.code === z.ZodIssueCode.too_small || issue.code === z.ZodIssueCode.invalid_type) {
        return { message: t('validation:accounts.name.required') };
      }
      if (issue.code === z.ZodIssueCode.too_big)
        return { message: t('validation:accounts.name.max') };
    }
    // integráció-sor: az üres külső azonosító (a sor `[integrations, i, externalId]` path-on jön)
    if (
      field === 'integrations' &&
      issue.path[2] === 'externalId' &&
      issue.code === z.ZodIssueCode.too_small
    ) {
      return { message: t('validation:accounts.integrationExternalId.required') };
    }
    // domain-sor: üres · nem domain-alakú · kétszer szerepel (a sor `[domains, i, domain]` path-on jön)
    if (field === 'domains' && issue.path[2] === 'domain') {
      const params = (issue as { params?: Record<string, unknown> }).params;
      if (params?.domainDuplicate === true) {
        return { message: t('validation:accounts.domain.duplicate') };
      }
      if (issue.code === z.ZodIssueCode.too_small) {
        return { message: t('validation:accounts.domain.required') };
      }
      return { message: t('validation:accounts.domain.invalid') };
    }
    if (field === 'roomCount' && issue.code === z.ZodIssueCode.custom) {
      return { message: t('validation:accounts.roomCount.invalid') };
    }
    // ── LEGACY-PARITÁS: koordináta-mezők. A `superRefine` a KÖLCSÖNÖS kötelezőséget jelöli (`coordinatePair`),
    //    a mező-szintű `refine` pedig a formátumot/tartományt. Kétféle üzenet, ugyanazon a mezőn.
    if ((field === 'latitude' || field === 'longitude') && issue.code === z.ZodIssueCode.custom) {
      const params = (issue as { params?: Record<string, unknown> }).params;
      if (params?.coordinatePair === true) {
        return { message: t('accounts.form.coordinatePairRequired') };
      }
      return { message: t('validation:accounts.coordinate.invalid') };
    }
    if (issue.code === z.ZodIssueCode.too_big) return { message: t('validation:accounts.tooLong') };
    return { message: ctx.defaultError };
  };
}
